/**
 * BootLoader — tela de carregamento única do boot da aplicação.
 *
 * O boot passa por vários estágios que renderizam uma tela de espera:
 * ThemeProvider (aguardando o tema da API), Suspense das rotas lazy,
 * RootRedirect (aguardando auth + portal) e ProtectedRoute (aguardando auth).
 * Antes da TMF9-61077 cada um pintava seu próprio fundo — e o do ProtectedRoute
 * não pintava nenhum, deixando aparecer o body branco. O resultado eram três a
 * quatro telas piscando em cores diferentes até a aplicação montar.
 *
 * A regra aqui é: todo estágio do boot usa ESTE componente, para que a troca de
 * estágio seja invisível. O fundo vem de CSS var — nunca de hook — porque o
 * ThemeProvider precisa renderizá-lo antes de o contexto de tema existir.
 *
 * @module components/ui
 * @since 2026-07-28
 * @issue TMF9-61077
 */

import { Spinner } from './Spinner';

/** Fundo compartilhado por todos os estágios do boot.
 *
 * Cascata: cor de login do tenant → cor da sidebar → azul institucional. O
 * último elo é o mesmo `#2a4d62` que o ThemeProvider usava fixo, mantido como
 * piso para o primeiro paint, quando ainda não há nenhuma var aplicada. */
export const BOOT_BACKGROUND = 'var(--login-bg, var(--sidebar, #2a4d62))';

interface BootLoaderProps {
  /**
   * Cobre a viewport inteira (`position: fixed`) em vez de ocupar o fluxo.
   * Usado pelo ThemeProvider, que renderiza antes de qualquer layout existir.
   */
  fullscreen?: boolean;
}

export function BootLoader({ fullscreen = false }: BootLoaderProps) {
  if (fullscreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: BOOT_BACKGROUND }}
        role="status"
        aria-live="polite"
        aria-label="Carregando aplicação"
      >
        <Spinner size="xl" color="white" />
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ backgroundColor: BOOT_BACKGROUND }}
      role="status"
      aria-live="polite"
      aria-label="Carregando aplicação"
    >
      <Spinner size="xl" color="white" />
    </div>
  );
}

export default BootLoader;
